import {EquipmentDataDropDownInput} from "../pages";
import {
    ASN_DESTINATION_NODE_TYPES,
    CLASS_ID,
    DEST_TO_SRC_EQ_TYPES,
    EMPTY_STRING,
    EQUIPMENT_TYPE,
    EQUIPMENT_TYPE_TO_CLASS,
    SRC_TO_DEST_EQ_TYPES
} from "./ConfigConstants";
import { getUniqueListBy } from "./Utils";
import { localTimezoneName } from "./DateUtil";
import { credentialStorage } from '@awsscm/argo-core/argo/utils/CredentialStorage';
import { SUPER_AZ_TO_AZ_MAP } from "../pages/lmdp/LmdpConstants";

export function getUniqueZones(eqData?: EquipmentDataDropDownInput[]): string[] {
    const uniqueZones: Set<string> = new Set();

    eqData?.forEach(value => {
        if(value.shippingZone !== null) {
            uniqueZones.add(value.shippingZone);
        }
    })

    return Array.from(uniqueZones) as string[];
}

export function getSiteNameFromEquipmentId(equipmentId?: string, eqData?: EquipmentDataDropDownInput[]): string {
    return eqData ?
        eqData.find(value => value.id === equipmentId)?.label || EMPTY_STRING
        : EMPTY_STRING;
}

export function getZoneNameFromEquipmentId(eqId?: string, eqData?: EquipmentDataDropDownInput[]): string {
  return eqData ?
    eqData.find(value => value.id === eqId)?.shippingZone || EMPTY_STRING
    : EMPTY_STRING;
}

export function getZoneFromSite(siteName?: string, eqData?: EquipmentDataDropDownInput[]): string {
    return eqData ?
        eqData.find(value => value.label === siteName)?.shippingZone || EMPTY_STRING
        : EMPTY_STRING;
}

export function getDCNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentType === EQUIPMENT_TYPE.DC) : [];
}

export function getCMNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentType === EQUIPMENT_TYPE.CM) : [];
}

export function getODMNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentType === EQUIPMENT_TYPE.ODM) : [];
}

export function getISHNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentType === EQUIPMENT_TYPE.ISH) : [];
}

export function getVMINodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentType === EQUIPMENT_TYPE.VMI) : [];
}

export function getAsnDestinationNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? handleMultipleNodes(eqData)
        .filter(value => ASN_DESTINATION_NODE_TYPES.includes(value.equipmentType ?? '')) : [];
}

export function getWarehouseAndVmiNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentType === EQUIPMENT_TYPE.WAREHOUSE ||
        value.equipmentType === EQUIPMENT_TYPE.VMI) : [];
}

export function getSupplierNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentVendorId) : [];
}

export function filterOutISHNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.equipmentType !== EQUIPMENT_TYPE.ISH) : [];
}

export function filterOutNonBookingNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? eqData.filter(value => value.isBookingSupported) : [];
}

export function getNonISHSupplierNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return filterOutISHNodes(getSupplierNodes(eqData));
}

export function getClusterNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? getUniqueListBy(eqData.filter(value => value.equipmentType === "CLUSTER")
        .map((equipment) => {return {...equipment, id:equipment.label, value:equipment.label}}),"label") : [];
}

export function getClusterNodesWithId(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? getUniqueListBy(eqData.filter(value => value.equipmentType === "CLUSTER"),"label") : [];
}

export function getAzNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? getUniqueListBy(eqData.filter(value => value.equipmentType === "AVAILABILITY_ZONE")
        .map((equipment) => {return {...equipment, id:equipment.label, value:equipment.label}}),"label") : [];
}

export function getSuperAzNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    return eqData? getUniqueListBy(convertAzToSuperAzNodes(eqData), "label") : [];
}

export function convertAzToSuperAzNodes(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    const AzToSuperAZMap: { [key: string]: string } = {};

    Object.entries(SUPER_AZ_TO_AZ_MAP).forEach(([key, values]) => {
        values.forEach(value => {
            AzToSuperAZMap[value] = key;
        });
    });

    return getAzNodes(eqData).map(equipment => {
        let superAz = "";
        if (equipment.id.startsWith("PDX") || equipment.id.startsWith("PDT")) {
            superAz = "PDX";
        } else if (equipment.id.startsWith("IAD")) {
            if (AzToSuperAZMap.hasOwnProperty(equipment.id)) {
                superAz = AzToSuperAZMap[equipment.id];
            } else {
                superAz = "IAD SOUTH";
            }
        } else {
            if (AzToSuperAZMap.hasOwnProperty(equipment.id)) {
                superAz = AzToSuperAZMap[equipment.id];
            } else {
                return equipment;
            }
        }
        return {
            ...equipment,
            id: superAz,
            value: superAz,
            label: superAz
        }
    });
}

function getBookingSupportedEquipments(eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    const filteredData = eqData? handleMultipleNodes(eqData) : [];
    const supplierNodes = getSupplierNodes(filteredData);
    const dcNodes = getDCNodes(filteredData);
    return filterOutNonBookingNodes(supplierNodes.concat(dcNodes));
}

export function getSourcesForDest(dest?: EquipmentDataDropDownInput, eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    const bookingEquipments = getBookingSupportedEquipments(eqData)
        .filter(eq =>  (eq.equipmentType?? '') in SRC_TO_DEST_EQ_TYPES);

    if (dest) {
        const possibleSrcTypes = DEST_TO_SRC_EQ_TYPES[dest.equipmentType!];
        return bookingEquipments.filter(eq => possibleSrcTypes.includes(eq.equipmentType!));
    }
    return bookingEquipments;
}

export function getDestinationsForSrc(src?: EquipmentDataDropDownInput, eqData?: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    const bookingEquipments = getBookingSupportedEquipments(eqData)
        .filter(eq =>  (eq.equipmentType?? '') in DEST_TO_SRC_EQ_TYPES);

    if (src) {
        const possibleDestTypes = SRC_TO_DEST_EQ_TYPES[src.equipmentType!];
        return bookingEquipments.filter(eq => possibleDestTypes.includes(eq.equipmentType!));
    }
    return bookingEquipments;
}

export function getTimezone(equipmentId?: string, eqData?: EquipmentDataDropDownInput[]): any {
    return eqData
        ?.find(eq => eq.id === equipmentId && eq.isBookingSupported && eq.timezone)?.timezone
        ?? localTimezoneName;
}

export function getCountry(equipmentId?: string, eqData?: EquipmentDataDropDownInput[]): string {
    return eqData
            ?.find(eq => eq.id === equipmentId)?.country
        ?? 'UNKNOWN';
}

export function filterVendorNodes(equipments: EquipmentDataDropDownInput[], loggedInVendorIds: string[]) {
    if (!credentialStorage.isExternalUser()) {
        return equipments;
    }
    return equipments.filter( (value) => { return value.equipmentVendorId && loggedInVendorIds.includes(value.equipmentVendorId!) })
}

// Returns only 1 equipment if there exists multiple equipments with same name and node type
export function handleMultipleEquipments(eqData: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    const nameAndNodeSet = new Set();

    return eqData.filter(equipment => {
        const key = equipment.label.concat(equipment.equipmentType?? '');

        if (!nameAndNodeSet.has(key)) {
            nameAndNodeSet.add(key);
            return true;
        }
        return false;
    });
}

// Returns only NodeV1 equipment if both SiteV3 and NodeV1 are present
export function handleMultipleNodes(eqData: EquipmentDataDropDownInput[]): EquipmentDataDropDownInput[] {
    const nameAndClassSet = new Set(
        eqData.map(equipment =>
            equipment.label.concat(getClassId(equipment))));

    return eqData.filter(equipment =>
        // Return false if current classId is SiteV3 & there already exists a NodeV1 equipment with same name
        !(CLASS_ID.SITE_V3 === getClassId(equipment)
            && nameAndClassSet.has(equipment.label.concat(CLASS_ID.SUPPLY_CHAIN_NODE_V1))));
}

/*
 * Below function denormalizes existing locations. Examples:
 * 1. Initial value(IAD001) => Updated value(IAD1).
 * 2. Initial value(IAD011) => Updated value(IAD11).
 * 3. Initial value(IAD01) => Updated value(IAD1).
 * 4. Initial value(IAD111) => Updated value(IAD111).
 */
export function denormalizeLocation(location: string) {
  if (location.length === 6) {
    if (location.substring(3, 5) === "00") {
      return location.substring(0, 3) + location.substring(5);
    } else if (location.substring(3, 4) === "0") {
      return location.substring(0, 3) + location.substring(4);
    }
  } else if (location.length === 5) {
    if (location.substring(3, 4) === "0") {
      return location.substring(0, 3) + location.substring(4);
    }    }
  return location;
}

function getClassId(equipment: EquipmentDataDropDownInput): string {
    return equipment.equipmentType ? EQUIPMENT_TYPE_TO_CLASS.get(equipment.equipmentType) as string : '';
}